/**
 * Image Transformation Utility
 * Provides functions for rotating and flipping images
 */

/**
 * Apply crop to an image
 * @param {string} base64Image - Base64 encoded image data
 * @param {Object} cropArea - Crop parameters (percentages 0-100)
 * @param {number} cropArea.x - X position as percentage
 * @param {number} cropArea.y - Y position as percentage
 * @param {number} cropArea.width - Width as percentage
 * @param {number} cropArea.height - Height as percentage
 * @returns {Promise<string>} - Cropped image as base64 string
 */
export async function applyCrop(base64Image, cropArea) {
  return new Promise((resolve, reject) => {
    if (!cropArea || cropArea.width <= 0 || cropArea.height <= 0) {
      resolve(base64Image);
      return;
    }

    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Convert percentages to pixels
      const sx = (cropArea.x / 100) * img.width;
      const sy = (cropArea.y / 100) * img.height;
      const sWidth = (cropArea.width / 100) * img.width;
      const sHeight = (cropArea.height / 100) * img.height;

      // Set canvas to cropped dimensions
      canvas.width = sWidth;
      canvas.height = sHeight;

      // Draw cropped portion
      ctx.drawImage(
        img,
        sx, sy, sWidth, sHeight,  // Source rectangle
        0, 0, sWidth, sHeight      // Destination rectangle
      );

      // Get the cropped image as base64
      const croppedBase64 = canvas.toDataURL('image/jpeg', 0.95);
      resolve(croppedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for cropping'));
    };

    img.src = base64Image;
  });
}

/**
 * Apply transformations to an image
 * @param {string} base64Image - Base64 encoded image data
 * @param {Object} transformations - Transformation parameters
 * @param {number} transformations.rotation - Rotation in degrees (0, 90, 180, 270)
 * @param {boolean} transformations.flipHorizontal - Whether to flip horizontally
 * @param {boolean} transformations.flipVertical - Whether to flip vertically
 * @param {Object} transformations.cropArea - Crop area (x, y, width, height as percentages)
 * @returns {Promise<string>} - Transformed image as base64 string
 */
export async function applyImageTransformations(base64Image, transformations = {}) {
  return new Promise((resolve, reject) => {
    const { rotation = 0, flipHorizontal = false, flipVertical = false, cropArea = null } = transformations;

    // If no transformations needed, return original
    if (rotation === 0 && !flipHorizontal && !flipVertical && !cropArea) {
      resolve(base64Image);
      return;
    }

    const img = new Image();

    img.onload = async () => {
      try {
        // Apply crop first if specified
        let processedImage = base64Image;
        if (cropArea && cropArea.width > 0 && cropArea.height > 0) {
          processedImage = await applyCrop(base64Image, cropArea);
        }

        // If no rotation or flip, return cropped image
        if (rotation === 0 && !flipHorizontal && !flipVertical) {
          resolve(processedImage);
          return;
        }

        // Load the processed (potentially cropped) image for rotation/flip
        const processedImg = new Image();
        processedImg.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate canvas dimensions based on rotation
          const isRotated = rotation === 90 || rotation === 270;
          const canvasWidth = isRotated ? processedImg.height : processedImg.width;
          const canvasHeight = isRotated ? processedImg.width : processedImg.height;

          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          // Move to center for transformations
          ctx.translate(canvasWidth / 2, canvasHeight / 2);

          // Apply rotation
          ctx.rotate((rotation * Math.PI) / 180);

          // Apply flips
          const scaleX = flipHorizontal ? -1 : 1;
          const scaleY = flipVertical ? -1 : 1;
          ctx.scale(scaleX, scaleY);

          // Draw the image centered
          ctx.drawImage(
            processedImg,
            -processedImg.width / 2,
            -processedImg.height / 2,
            processedImg.width,
            processedImg.height
          );

          // Get the transformed image as base64
          const transformedBase64 = canvas.toDataURL('image/jpeg', 0.95);
          resolve(transformedBase64);
        };
        processedImg.onerror = () => {
          reject(new Error('Failed to load processed image'));
        };
        processedImg.src = processedImage;
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for transformation'));
    };

    img.src = base64Image;
  });
}

/**
 * Convert base64 to File object
 * @param {string} base64String - Base64 encoded image data
 * @param {string} filename - Name for the file
 * @returns {File} - File object
 */
export function base64ToFile(base64String, filename = 'image.jpg') {
  // Remove data URL prefix if present
  const base64Data = base64String.includes(',')
    ? base64String.split(',')[1]
    : base64String;

  // Decode base64
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Determine MIME type
  const mimeType = base64String.match(/^data:([^;]+);/)?.[1] || 'image/jpeg';

  return new File([bytes], filename, { type: mimeType });
}

/**
 * Apply step transformation (for incremental updates)
 * @param {string} base64Image - Current image data
 * @param {Object} currentTransform - Current transformation state
 * @param {Object} newTransform - New transformation to apply
 * @returns {Promise<Object>} - Updated image and transformation state
 */
export async function applyStepTransformation(base64Image, currentTransform, newTransform) {
  let { rotation = 0, flipHorizontal = false, flipVertical = false } = currentTransform;

  // Apply new transformation
  if (newTransform.rotate) {
    rotation = (rotation + 90) % 360;
  }
  if (newTransform.flipHorizontal !== undefined) {
    flipHorizontal = !flipHorizontal;
  }
  if (newTransform.flipVertical !== undefined) {
    flipVertical = !flipVertical;
  }

  // Apply all transformations from original image
  const transformedImage = await applyImageTransformations(base64Image, {
    rotation,
    flipHorizontal,
    flipVertical
  });

  return {
    image: transformedImage,
    transformations: {
      rotation,
      flipHorizontal,
      flipVertical
    }
  };
}